/* global axios */

import Cookies from 'js-cookie';
import endPoints from './endPoints';
import {
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../store/utils/api';

export default {
  validityCheck() {
    const urlData = endPoints('validityCheck');
    return axios.get(urlData.url);
  },
  async logout() {
    // Best-effort cross-instance sign-out. We capture the Cognito ID token
    // FIRST (while the session is still valid), then kick off the fan-out
    // POSTs to every known sibling instance WITHOUT awaiting them. Each
    // fetch uses `keepalive: true` so the browser completes it even after
    // we navigate away on local logout below. This keeps the perceived
    // logout latency bounded to the local logout round-trip instead of
    // waiting for a slow sibling instance to respond.
    let idToken = null;
    try {
      const { data } = await axios.get('/auth/cognito/id_token');
      idToken = data?.id_token;
    } catch (e) {
      // No Cognito token (dev mode, expired session, etc) — skip fan-out.
    }

    if (idToken) {
      // Fire-and-forget: do NOT await.
      this.fanOutCognitoSignOut(idToken).catch(() => {});
    }

    // Always clear local state and navigate — even if the server-side
    // DELETE fails (network down, 5xx, etc). The user clicked logout;
    // we must not leave them stuck on the dashboard with a half-alive
    // session. `clearCookiesOnLogout` handles the navigation via
    // `window.location = logoutRedirectLink`, so this function does not
    // throw back to the caller under any path.
    let response = null;
    try {
      const urlData = endPoints('logout');
      response = await axios.delete(urlData.url);
    } catch (e) {
      // swallow — local cleanup + navigation below will still run
    }
    deleteIndexedDBOnLogout();
    clearCookiesOnLogout();
    return response;
  },
  fanOutCognitoSignOut(idToken) {
    const instances =
      window.WOOT?.$store?.getters?.getAvailableInstances || [];
    if (instances.length === 0) return Promise.resolve();

    // `application/x-www-form-urlencoded` (via URLSearchParams) is a CORS
    // "simple request" — no preflight — so the POST goes through even though
    // the target instance's CORS policy doesn't allowlist /auth/cognito/*.
    // `keepalive: true` guarantees the request survives the page navigation
    // that the local logout triggers right after.
    const body = new URLSearchParams({ id_token: idToken });
    return Promise.allSettled(
      instances.map(inst => {
        const host = (inst.instanceHost || '').replace(/\/$/, '');
        if (!host) return Promise.resolve();
        const origin = /^https?:\/\//i.test(host) ? host : `https://${host}`;
        return fetch(`${origin}/auth/cognito/sign_out_all`, {
          method: 'POST',
          body,
          mode: 'no-cors',
          keepalive: true,
        });
      })
    );
  },
  hasAuthCookie() {
    return !!Cookies.get('cw_d_session_info');
  },
  getAuthData() {
    if (this.hasAuthCookie()) {
      const savedAuthInfo = Cookies.get('cw_d_session_info');
      return JSON.parse(savedAuthInfo || '{}');
    }
    return false;
  },
  profileUpdate({
    password,
    password_confirmation,
    displayName,
    avatar,
    ...profileAttributes
  }) {
    const formData = new FormData();
    Object.keys(profileAttributes).forEach(key => {
      const hasValue = profileAttributes[key] === undefined;
      if (!hasValue) {
        formData.append(`profile[${key}]`, profileAttributes[key]);
      }
    });
    formData.append('profile[display_name]', displayName || '');
    if (password && password_confirmation) {
      formData.append('profile[password]', password);
      formData.append('profile[password_confirmation]', password_confirmation);
    }
    if (avatar) {
      formData.append('profile[avatar]', avatar);
    }
    return axios.put(endPoints('profileUpdate').url, formData);
  },

  updateUISettings({ uiSettings }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: { ui_settings: uiSettings },
    });
  },

  updateAvailability(availabilityData) {
    return axios.post(endPoints('availabilityUpdate').url, {
      profile: { ...availabilityData },
    });
  },

  updateAutoOffline(accountId, autoOffline = false) {
    return axios.post(endPoints('autoOffline').url, {
      profile: { account_id: accountId, auto_offline: autoOffline },
    });
  },

  deleteAvatar() {
    return axios.delete(endPoints('deleteAvatar').url);
  },

  resetPassword({ email }) {
    const urlData = endPoints('resetPassword');
    return axios.post(urlData.url, { email });
  },

  setActiveAccount({ accountId }) {
    const urlData = endPoints('setActiveAccount');
    return axios.put(urlData.url, {
      profile: {
        account_id: accountId,
      },
    });
  },
  resendConfirmation() {
    const urlData = endPoints('resendConfirmation');
    return axios.post(urlData.url);
  },

  getProfileInstances() {
    const urlData = endPoints('profileInstances');
    return axios.get(urlData.url);
  },
};
